import type { StaticImageData } from 'next/image';
import { useRouter } from 'next/router';
import ErrorSvg from 'public/img/illustrations/error-document.svg';
import { type ReactNode, useEffect } from 'react';
import { validate as isUuid } from 'uuid';
import { useInteraction } from '@/common/hooks/useInteraction';
import { useInteractionParams } from '@/common/hooks/useInteractionParams';
import { Spinner } from '@/components/Spinner';
import { WholePageError } from '@/components/WholePageError';
import { interactionTypeRootPathName } from '@/helpers';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  errorFallback?: ReactNode;
}

type InteractionError = ReturnType<typeof useInteraction>['error'];

const isClientError = (error: InteractionError): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  return error.statusCode >= 400 && error.statusCode < 500;
};

/**
 * A component that renders its children if the interaction type and ID
 * specified in the route path are valid.
 *
 * An interaction type/ID combination is considered "valid" if either:
 * - The interaction ID or type is empty (i.e. we're not on an interaction
 * detail page), OR
 * - The interaction type given in the URL matches the type returned from
 * core service for the given interaction ID
 *
 * The user is redirected to the interaction error page only when the
 * interaction is definitively invalid: a non-UUID (legacy) id — which
 * short-circuits before any fetch, since legacy ids resolve in core-service
 * but error out in payment-service — or a 4xx response. Server/network
 * failures (e.g. a core-service outage) render an in-place error state
 * instead, so a transient outage doesn't strand users on the error page.
 * The provided fallback content is displayed while the id/type are being
 * validated and during the redirect.
 */
export function WithValidInteraction({
  children,
  fallback = <Spinner centered fullScreen />,
  errorFallback = (
    <WholePageError svg={ErrorSvg as StaticImageData} sx={{ mt: 10 }} />
  ),
}: Props) {
  const { push } = useRouter();
  const { interactionId, interactionType, childInteractionId } =
    useInteractionParams();

  const hasInteraction = !!interactionId && !!interactionType;
  const isNonUuidId = !!interactionId && !isUuid(interactionId);
  const isNonUuidChildId = !!childInteractionId && !isUuid(childInteractionId);
  const hasNonUuidId = isNonUuidId || isNonUuidChildId;

  const skip = !hasInteraction || hasNonUuidId;

  const { data, error, isLoading } = useInteraction(
    interactionId,
    interactionType,
    skip,
  );

  const isInvalidInteraction = hasNonUuidId || isClientError(error);

  useEffect(() => {
    if (!hasInteraction) return;
    if (!isInvalidInteraction) return;

    void push(
      `/${interactionTypeRootPathName(interactionType)}/${interactionId}/interaction-error`,
    );
  }, [
    hasInteraction,
    isInvalidInteraction,
    interactionId,
    interactionType,
    push,
  ]);

  if (isInvalidInteraction) {
    return fallback;
  }

  if (isLoading) {
    return fallback;
  }

  if (error !== undefined) {
    return errorFallback;
  }

  if (!data) {
    return fallback;
  }

  return children;
}
